<template>
  <v-container fluid style="max-width: 1185px;">

    <v-row class="px-6" v-touch="{ right: () => step(-1), left: () => step(1) }">
      <v-col
        cols="4"
        v-for="(slot, i) in slots" :key="slot.id"
        class="d-flex justify-center" style="z-index: 2;"
      >
        <v-responsive :aspect-ratio="5 / 7" max-width="220" class="overflow-visible">
          <!-- The actual link / click handlers are attached to the static container card that doesn't move to make sure we don't run afoul of CLS -->
          <v-card
            class="absolute-fill"
            flat color="transparent"
            :to="slot.to"
          >
            <v-card
              class="shelf-card front overflow-hidden"
              :class="`anim-${i}`"
              v-bind:style="{ 'animation-play-state': !isGroupLoaded ? 'paused' : 'running' }"
              hover tile
            >
              <video
                v-if="slot.pages[0].mp4"
                playsinline autoplay muted loop
                :src="slot.pages[0].mp4"
                @loadedmetadata="loaded(i + '-0')"
              />
              <img v-else :src="slot.pages[0].jpg" @load="loaded(i + '-0')" />
            </v-card>
            <v-card
              class="shelf-card back overflow-hidden"
              :class="`anim-${i}`"
              v-bind:style="{ 'animation-play-state': !isGroupLoaded ? 'paused' : 'running' }"
              style="transform: rotateY(180deg);"
              hover tile
            >
              <video
                v-if="slot.pages[1].mp4"
                playsinline autoplay muted loop
                :src="slot.pages[1].mp4"
                @loadedmetadata="loaded(i + '-1')"
              />
              <img v-else :src="slot.pages[1].jpg" @load="loaded(i + '-1')" />
            </v-card>
          </v-card>
        </v-responsive>
      </v-col>
    </v-row>

    <div class="shelf"></div>

    <!-- How to make the shelf dynamically -->
    <!--
    <div style="bottom: 20%; height: 1%; left: 4%; right: 4%; transform: perspective(3px) rotateX(3deg);" class="shelf top" />
    <div style="bottom: 10%; height: 10%; left: 2.4%; right: 2.4%;" class="shelf back" />
    <div style="bottom: 3%; height: 8%; left: -2%; right: -2%; transform: perspective(3px) rotateX(1deg); box-shadow: inset 0px 0px 15px 0px #403e3d, 0px 2px 40px -10px #403e3d;" class="shelf top" />
    <div style="bottom: 0; height: 2.5%; left: -7.7%; right: -7.7%;" class="shelf back" />
    -->

  </v-container>
</template>


<style scoped>
.shelf-card {
  background: #FFF;
  position: absolute;
  pointer-events: auto;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  animation-direction: normal, reverse;
  animation-duration: 1s, 1s;
  animation-fill-mode: forwards, forwards;
}
.shelf-card > * {
  width: 100%;
  height: 100%;
}

.shelf-card.front {
  animation-name: flip-front, flip-front;
}
.shelf-card.back {
  animation-name: flip-back, flip-back;
}
.shelf-card.anim-0 {
  animation-delay: 3s, 7s;
}
.shelf-card.anim-1 {
  animation-delay: 3.5s, 7s;
}
.shelf-card.anim-2 {
  animation-delay: 4s, 7s;
}
@keyframes flip-front {
  0% { transform: rotateY(0deg); }
  100% { transform: rotateY(180deg); }
}
@keyframes flip-back {
  0% { transform: rotateY(180deg); }
  100% { transform: rotateY(0deg); }
}

.shelf {
  position: relative;
  z-index: 1;
  margin-top: -28px;
  width: 100%;
  border-bottom-image-source: linear-gradient(to left, #743ad5, #d53a9d);
  border-bottom: 32px solid #DEDEDE;
  border-left: 24px solid transparent;
  border-right: 24px solid transparent;
}

.shelf:after {
  content: '';
  background: linear-gradient(#FFF, #333);
  height: 20px;
  position: absolute;
  top: 32px;
  left: -2px;
  right: -2px;
  z-index: 1;
  margin: 0 -24px;
  border-radius: 4px / 16px;
  box-shadow: 0 12px 16px -4px #0008;
}
</style>


<script>
// Make sure the number of examples is divisible by 3
// You reference these by index in: https://cdn.ellacard.com/start/card/[id]-[front|back].[type]
// Generate the compressed card resources using:
// ffmpeg -i input -vf scale=-1:322 output.mp4
const EXAMPLES = [
  { id: 'd76bd26a-595d-47ad-8863-ab121ae7f33e', exts: [ 'mp4', 'jpg' ], q: [ 'Birthday', 'dog' ] },
  { id: '3ae0b078-e7e2-428c-8c10-55e1bc7069b0', exts: [ 'jpg', 'jpg' ], q: [ 'Thank You', 'coffee' ] },
  { id: '4256a356-caa4-423a-b85a-5ffd87a88f9e', exts: [ 'mp4', 'jpg' ], q: [ 'Get Well', 'dove' ] },
  { id: '8436edde-158c-4f77-9300-4aae34648900', exts: [ 'mp4', 'jpg' ], q: [ 'Farewell', 'monsters' ] },
  { id: '95fc1916-7a7b-4b3b-a354-27a8bc15350f', exts: [ 'mp4', 'jpg' ], q: [ 'Birthday', 'cake' ] },
  { id: '28d85ce2-ee34-4e96-8ded-c61973ee2196', exts: [ 'mp4', 'jpg' ], q: [ 'Baby', 'boy', 'elephant' ] }
];

export default {
  name: 'card-shelf',

  props: {
    examples: {
      type: Array,
      default: () => EXAMPLES.map(({ id, exts, q }) => ({
        id,
        pages: exts.map(
          (x, i) => ({ [ext]: `https://cdn.ellacard.com/start/card/${id}-${[ 'front', 'back' ][i]}.${x}` })
        ),
        to: { name: 'catalog', params: { selectedId: id }, query: { q } }
      }))
    }
  },

  data() {
    return {
      group: 0,
      slots: [],
      isGroupLoaded: false
    };
  },

  destroyed() {
    clearTimeout(this.t);
  },

  methods: {
    step(d) {
      // 'd' cannot be greater than the number of groups (presumably should only be +/- 1)
      this.group = (this.examples.length / 3 + this.group + d) % (this.examples.length / 3);
      this.isGroupLoaded = false;
    },

    loaded(key) {
      this.loadedKeys.add(key);
      if (this.loadedKeys.size == 3 * 2) {
        // All the columns in this group have been loaded, now we can start the animations and start the timer
        this.isGroupLoaded = true;
        this.t = setTimeout(() => this.step(1), 12000);
      }
    }
  },

  watch: {
    group: {
      immediate: true,
      handler() {
        this.loadedKeys = new Set();
        this.slots = Array(3).fill(null).map((_, i) => this.examples[3 * this.group + i]);
      }
    }
  }
}
</script>
